// internal-deps
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/core-ui";

// constants
import { CLEVERTAP_KEYS } from "@cart-client/constants/Clevertap";

// interfaces
import { CartItem, IMStructureItem } from "@swiggy-private/cart-types";

// helpers
import { isUserLoggedIn } from "./User";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { getIMItemFinalPrice } from "./GiftingIMSubCart";

export const logCartViewedEvent = (cartData: GiftingReviewedCart): void => {
    if (isEmpty(cartData.data)) {
        return;
    }
    let superPlanType = "";
    const cartMenuItems = [...(cartData.data.foodCartItems?.items ?? [])];
    const imCartItems = [...(cartData.data?.imCartItems ?? [])];

    const subsItems = cartData.data.subscription_items ?? [];
    const cartItems = [...cartMenuItems, ...imCartItems].sort((itemA, itemB) => {
        const itemAFinalPrice =
            Number((itemA as CartItem).final_price) ||
            Number(getIMItemFinalPrice(itemA as IMStructureItem).slice(1)) * 100;
        const itemBFinalPrice =
            Number((itemB as CartItem).final_price) ||
            Number(getIMItemFinalPrice(itemB as IMStructureItem).slice(1)) * 100;
        return itemBFinalPrice - itemAFinalPrice;
    });

    const itemsQuantity = cartItems.reduce((acc, item) => acc + item.quantity, 0);

    subsItems.map((item) => {
        superPlanType += item.name;
    });

    const taxAndCharges = (cartData.data.rendering_details ?? []).find(
        (item) => item.key === "taxes_and_charges",
    )?.value;

    const freeDelUnlocked = cartData.data.free_delivery_discount && cartData.data.free_delivery_discount > 0;

    const isCouponApplied = cartData.data.coupon_details?.is_coupon_valid == true;
    const couponPotentialSavings =
        cartData.data.apply_coupon_widget_info?.coupon_codes?.reduce(
            (acc, coupon) => acc + Number(coupon.savings),
            0,
        ) ?? 0;
    const superSpecificDiscount = Math.floor(cartData.data.super_specific_discount ?? 0);
    const totalDiscount = cartData.data.discount_message_v2?.message_details?.total_discount ?? 0;
    const totalPotentialSavings = isCouponApplied ? totalDiscount : couponPotentialSavings + superSpecificDiscount;

    const clevertapParams: { [key: string]: unknown } = {
        [CLEVERTAP_KEYS.ITEM_TOTAL]: cartData.data.item_total,
        [CLEVERTAP_KEYS.TAXES_AND_CHARGES]: taxAndCharges ?? "NULL",
        [CLEVERTAP_KEYS.DEL_FEE]: cartData.data.delivery_charges,
        [CLEVERTAP_KEYS.SURGE_FEE]: cartData.data.special_fee,
        [CLEVERTAP_KEYS.TOTAL_DISCOUNT]: cartData.data.discount_total,
        [CLEVERTAP_KEYS.TOTAL_BILL_VALUE]: cartData.data.cart_total,
        [CLEVERTAP_KEYS.COUPON_APPLIED_FLAG]: isCouponApplied ? "Yes" : "No",
        [CLEVERTAP_KEYS.COUPON_NAME]:
            cartData.data.coupon_details?.is_coupon_valid == true
                ? cartData.data.couponCodes?.reduce((acc, coupon) => `${acc}${acc ? "," : ""}${coupon.code}`, "")
                : "NULL",
        [CLEVERTAP_KEYS.IS_SWIGGY_ONE]: cartData.data.user_super_status_details?.user_status === "SUPER",
        [CLEVERTAP_KEYS.QUANTITY]: itemsQuantity,
        [CLEVERTAP_KEYS.ADDRESS_ID]: cartData.data.address_id ?? "0",
        [CLEVERTAP_KEYS.CART_ID]: cartData.data.cartId ?? "0",
        [CLEVERTAP_KEYS.ONE_PLAN_ADDED]: subsItems.length > 0,
        [CLEVERTAP_KEYS.ONE_PLAN_DURATION_ADDED]: isEmpty(superPlanType) ? "NULL" : superPlanType,
        [CLEVERTAP_KEYS.ONE_FREE_DELIVERY_UNLOCKED]: freeDelUnlocked,
        [CLEVERTAP_KEYS.ONE_FREE_DELIVERY_SAVINGS]: Math.floor(cartData.data.free_delivery_discount ?? 0),
        [CLEVERTAP_KEYS.ONE_SAVINGS]: Math.floor(cartData.data.super_specific_discount ?? 0),
        [CLEVERTAP_KEYS.COUPON_CODE_CART]: cartData.data.apply_coupon_widget_info?.coupon_code ?? "NULL",
        [CLEVERTAP_KEYS.COUPON_DISCOUNT_SAVINGS]: cartData.data.apply_coupon_widget_info?.savings ?? "NULL",
        [CLEVERTAP_KEYS.TOTAL_POTENTIAL_SAVINGS]: totalPotentialSavings,
        [CLEVERTAP_KEYS.IS_GUEST_USER]: !isUserLoggedIn(),
    };

    // top 3 cart items to be added in list
    cartItems.map((item, index) => {
        if (index < 3) {
            clevertapParams[`item ${index + 1} name`] = item.name ?? "NULL";
            clevertapParams[`item ${index + 1} quantity`] = item.quantity?.toString() ?? "NULL";
            if ("menu_item_id" in item) {
                clevertapParams[`item ${index + 1} id`] = item.menu_item_id?.toString() ?? "NULL";
                clevertapParams[`item ${index + 1} url`] = item.cloudinaryImageId ?? "NULL";
                clevertapParams[`item ${index + 1} type`] = "food";
            } else if ("itemId" in item) {
                clevertapParams[`item ${index + 1} id`] = item.itemId?.toString() ?? "NULL";
                clevertapParams[`item ${index + 1} url`] = item.imageIds?.[0] ?? "NULL";
                clevertapParams[`item ${index + 1} type`] = "im";
            }
        }
    });

    getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.GIFTING_CART_VIEWED_EVENT, params: clevertapParams });
};
